/**
 * Airtable Service
 * Handles all interactions with the Airtable API for the URL shortener
 */

import Airtable from "airtable";

// Airtable configuration using environment variables
// These are injected by Netlify during build time
const AIRTABLE_API_KEY = process.env.REACT_APP_AIRTABLE_API_KEY;
const AIRTABLE_BASE_ID = process.env.REACT_APP_AIRTABLE_BASE_ID;
const TABLE_NAME = process.env.REACT_APP_AIRTABLE_TABLE_ID;

// Check if environment variables are loaded
if (!AIRTABLE_API_KEY || !AIRTABLE_BASE_ID || !TABLE_NAME) {
  console.error("Missing Airtable environment variables. Please check your configuration.");
}

// Initialize Airtable
const base = new Airtable({ apiKey: AIRTABLE_API_KEY }).base(AIRTABLE_BASE_ID);
const table = base(TABLE_NAME);

/**
 * Creates a new shortened URL record
 * @param {Object} urlData - Data for the new URL record
 * @param {string} urlData.shortCode - Generated short code
 * @param {string} urlData.originalurl - Original long URL
 * @param {string} urlData.country - Country (Malaysia, Singapore, Cambodia)
 * @param {string} urlData.channel - Channel of the URL (e.g., FB, IG)
 * @param {string} urlData.sources - Source type (e.g., Recipe, SEO)
 * @param {string} urlData.pic - Person in charge
 * @param {string} urlData.campaignname - Campaign name
 * @param {string} urlData.fullshorturl - Complete shortened URL
 * @param {string} urlData.fullutmurl - URL with UTM parameters
 * @param {string} urlData.thumbnailUrl - Custom thumbnail image URL (optional)
 * @param {string} urlData.metaTitle - Custom meta title for preview (optional)
 * @param {string} urlData.metaDescription - Custom meta description for preview (optional)
 * @returns {Promise<Object>} - Created record
 */
export const createUrl = async (urlData) => {
  try {
    // Log the data we're trying to save
    console.log("Creating URL with data:", urlData);

    // Create record with all required fields, using exact field names from Airtable
    const recordData = {
      shortCode: urlData.shortCode,
      originalurl: urlData.originalurl,
      country: urlData.country,
      channel: urlData.channel, // Updated from source to channel
      sources: urlData.sources, // New field for sources (Recipe, SEO, etc.)
      pic: urlData.pic,
      campaignname: urlData.campaignname,
      status: urlData.status || "Active",
      fullshorturl: urlData.fullshorturl,
      fullutmurl: urlData.fullutmurl,
      clicks: 0, // Initialize click counter at 0 (as a number not a string)
    };

    // Add optional thumbnail and meta fields if provided
    if (urlData.thumbnailUrl) {
      recordData.thumbnailUrl = urlData.thumbnailUrl;
    }
    
    if (urlData.metaTitle) {
      recordData.metaTitle = urlData.metaTitle;
    }
    
    if (urlData.metaDescription) {
      recordData.metaDescription = urlData.metaDescription;
    }

    console.log("Sending to Airtable:", recordData);

    const record = await table.create(recordData);
    console.log("Record created successfully:", record);

    return {
      id: record.id,
      ...record.fields,
    };
  } catch (error) {
    // Log the detailed error
    console.error("Error creating URL in Airtable:", error);

    // If it's an Airtable error, extract and log the message
    if (error.message) {
      console.error("Error message:", error.message);
    }

    if (error.error) {
      console.error("Error details:", error.error);
    }

    throw new Error("Failed to create shortened URL");
  }
};

/**
 * Helper function to parse and convert Airtable date-time strings to JavaScript Date objects
 * @param {string} dateTimeStr - Date time string in format "DD/MM/YYYY h:mmam/pm"
 * @returns {Date|null} - JavaScript Date object or null if invalid
 */
const parseAirtableDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return null;

  try {
    // Handle format "27/2/2025 9:43pm"
    const parts = dateTimeStr.split(" ");
    if (parts.length !== 2) return new Date(dateTimeStr); // Fallback to standard parsing

    const datePart = parts[0]; // "27/2/2025"
    const timePart = parts[1]; // "9:43pm"

    // Parse date part "27/2/2025" to "2025-02-27"
    const datePieces = datePart.split("/");
    if (datePieces.length !== 3) return new Date(dateTimeStr); // Fallback

    const day = datePieces[0].padStart(2, "0");
    const month = datePieces[1].padStart(2, "0");
    const year = datePieces[2];

    // Parse time part "9:43pm"
    const isPM = timePart.toLowerCase().includes("pm");
    const timeOnly = timePart.toLowerCase().replace("am", "").replace("pm", "");
    const timePieces = timeOnly.split(":");

    if (timePieces.length !== 2) return new Date(dateTimeStr); // Fallback

    let hour = parseInt(timePieces[0], 10);
    const minute = timePieces[1].padStart(2, "0");

    // Convert to 24-hour format if PM
    if (isPM && hour < 12) hour += 12;
    if (!isPM && hour === 12) hour = 0;

    hour = hour.toString().padStart(2, "0");

    // Create ISO date string: YYYY-MM-DDTHH:MM:SS
    const isoDateString = `${year}-${month}-${day}T${hour}:${minute}:00`;

    return new Date(isoDateString);
  } catch (error) {
    console.error("Error parsing date:", dateTimeStr, error);
    // Return fallback
    return new Date(dateTimeStr);
  }
};

/**
 * Fetches all URLs, filtering out ones marked as deleted
 * @returns {Promise<Array<Object>>} - Array of URL records
 */
export const getAllUrls = async () => {
  try {
    console.log("Fetching all URLs");

    // Get all records
    const records = await table.select().all();
    console.log(`Got ${records.length} records from Airtable`);

    // Map records to objects and add their IDs
    const allUrls = records.map((record) => ({
      id: record.id,
      ...record.fields,
    }));

    // Filter out deleted URLs
    const activeUrls = allUrls.filter((url) => url.status !== "Deleted");

    // Log a few timestamps for debugging
    if (activeUrls.length > 0) {
      const sampleUrls = activeUrls.slice(0, Math.min(3, activeUrls.length));
      sampleUrls.forEach((url) => {
        if (url.createdtime) {
          const parsedDate = parseAirtableDateTime(url.createdtime);
          console.log(
            `URL ${url.shortCode} timestamp: ${url.createdtime} (parsed: ${
              parsedDate ? parsedDate.toISOString() : "invalid"
            })`
          );
        }
      });
    }

    // Sort by createdtime (newest first) - using enhanced parsing
    activeUrls.sort((a, b) => {
      // First try to sort by createdtime with proper parsing
      if (a.createdtime && b.createdtime) {
        const dateA = parseAirtableDateTime(a.createdtime);
        const dateB = parseAirtableDateTime(b.createdtime);

        // Check if dates are valid before comparing
        if (
          dateA &&
          dateB &&
          !isNaN(dateA.getTime()) &&
          !isNaN(dateB.getTime())
        ) {
          return dateB.getTime() - dateA.getTime(); // Newest first
        }
      }

      // Fallback to record ID if createdtime isn't available or valid
      if (a.id && b.id) {
        // Assume newer records have "higher" IDs
        return b.id.localeCompare(a.id);
      }

      return 0; // If all else fails, maintain original order
    });

    console.log(
      `Returning ${activeUrls.length} active URLs, sorted by creation time`
    );
    return activeUrls;
  } catch (error) {
    console.error("Error fetching URLs from Airtable:", error);
    // Return empty array instead of throwing
    return [];
  }
};

/**
 * Fetches a URL by its short code
 * @param {string} shortCode - Short code to look up
 * @returns {Promise<Object|null>} - URL record or null if not found
 */
export const getUrlByShortCode = async (shortCode) => {
  try {
    console.log(`Looking up URL with short code: ${shortCode}`);

    const records = await table
      .select({
        filterByFormula: `{shortCode} = '${shortCode}'`,
        maxRecords: 1,
      })
      .all();

    console.log(`Found ${records.length} records for short code: ${shortCode}`);

    if (records.length === 0) {
      console.log(`No record found for short code: ${shortCode}`);
      return null;
    }

    console.log(`Record found with ID: ${records[0].id}`);
    console.log(`Record fields:`, records[0].fields);

    return {
      id: records[0].id,
      ...records[0].fields,
    };
  } catch (error) {
    console.error(`Error fetching URL for short code ${shortCode}:`, error);
    console.error("Error message:", error.message);
    console.error("Error stack:", error.stack);
    return null;
  }
};

/**
 * Checks if a short code already exists
 * @param {string} shortCode - Short code to check
 * @returns {Promise<boolean>} - Whether the short code exists
 */
export const shortCodeExists = async (shortCode) => {
  try {
    const url = await getUrlByShortCode(shortCode);
    return !!url;
  } catch (error) {
    console.error("Error checking if short code exists:", error);
    return false;
  }
};

/**
 * Deletes a URL (soft delete by changing status)
 * @param {string} id - Record ID to delete
 * @returns {Promise<void>}
 */
export const deleteUrl = async (id) => {
  try {
    await table.update(id, {
      status: "Deleted",
    });
  } catch (error) {
    console.error("Error deleting URL:", error);
    throw new Error("Failed to delete shortened URL");
  }
};

/**
 * Increments the click count for a specific URL
 * @param {string} id - Record ID of the URL to update
 * @returns {Promise<Object>} - Updated record
 */
export const incrementUrlClicks = async (id) => {
  try {
    console.log(`========= CLICK INCREMENT OPERATION =========`);
    console.log(`Starting click increment for URL ID: ${id}`);

    // First get the current record to check its current click count
    console.log(`Fetching current record from Airtable with ID: ${id}`);
    let record;
    try {
      record = await table.find(id);
      console.log(`Successfully retrieved record with ID: ${id}`);
    } catch (findError) {
      console.error(`Error finding record with ID ${id}:`, findError);
      console.error("Find error details:", findError.message, findError.stack);
      throw new Error(`Failed to find URL record: ${findError.message}`);
    }

    if (!record) {
      console.error(`URL record with ID ${id} not found`);
      throw new Error(`URL with ID ${id} not found`);
    }

    console.log(`Record found with ID: ${record.id}`);
    console.log(`Record fields:`, record.fields);

    // Check if clicks field exists
    if (record.fields.clicks === undefined) {
      console.log(`Clicks field doesn't exist yet. Setting initial value to 0`);
    }

    // Get current click count or default to 0
    const currentClicks = parseInt(record.fields.clicks || "0", 10);
    const newClicks = currentClicks + 1;

    console.log(`Current clicks: ${currentClicks}, New clicks: ${newClicks}`);

    // Prepare update data - send as a number, not a string
    const updateData = {
      clicks: newClicks, // Send as a number, not newClicks.toString()
    };

    console.log(`Sending update to Airtable with data:`, updateData);

    // Update the record with the new click count
    let updatedRecord;
    try {
      updatedRecord = await table.update(id, updateData);
      console.log(`Update successful!`);
      console.log(`Updated record ID: ${updatedRecord.id}`);
      console.log(`Updated fields:`, updatedRecord.fields);
    } catch (updateError) {
      console.error(`Error updating click count for ID ${id}:`, updateError);
      console.error(
        "Update error details:",
        updateError.message,
        updateError.stack
      );

      // More details about the error
      if (updateError.error) {
        console.error("Error object:", updateError.error);
      }

      // Attempt to log the specific Airtable error if available
      if (updateError.statusCode) {
        console.error("Status code:", updateError.statusCode);
      }

      throw new Error(`Failed to update click count: ${updateError.message}`);
    }

    console.log(`Click increment complete for URL ID: ${id}`);
    console.log(`New click count: ${updatedRecord.fields.clicks || "unknown"}`);
    console.log(`========= END CLICK INCREMENT OPERATION =========`);

    return {
      id: updatedRecord.id,
      ...updatedRecord.fields,
    };
  } catch (error) {
    console.error(`========= CLICK INCREMENT FAILED =========`);
    console.error(`Error incrementing URL clicks for ID ${id}:`, error);
    console.error("Error message:", error.message);
    console.error("Error stack:", error.stack);
    console.error(`========= END CLICK INCREMENT ERROR =========`);
    throw error; // Rethrow to allow handling in the redirect handler
  }
};

export default {
  createUrl,
  getAllUrls,
  getUrlByShortCode,
  shortCodeExists,
  deleteUrl,
  incrementUrlClicks,
};
